// [claude-code 2026-03-24] Supabase client + Google OAuth for Electron deep link flow
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import {
  buildAuthCallbackUrl,
  isElectronRuntime,
  type AuthFlowMode,
} from "../../shared/auth-redirect";
import { getRuntimeApiBase } from "./runtime-api-base";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabasePublicKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const DEFAULT_AUTH_ORIGIN = "https://fintheon.pricedinresearch.io";

let supabase: SupabaseClient | null = null;

if (supabaseUrl && supabasePublicKey && !supabasePublicKey.startsWith("<")) {
  supabase = createClient(supabaseUrl, supabasePublicKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false, // We handle deep links manually
    },
  });
}

export { supabase };

/**
 * Start Google OAuth flow via Supabase.
 * Uses skipBrowserRedirect so we can open the URL in the system browser
 * (Electron) and receive the callback via fintheon:// deep link.
 */
interface EmailPasswordAuthInput {
  email: string;
  password: string;
  mode: AuthFlowMode;
}

export async function signInWithGoogle(
  mode: AuthFlowMode = "login",
  relayId?: string,
) {
  if (!supabase)
    throw new Error(
      "Supabase not configured — check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY or VITE_SUPABASE_PUBLISHABLE_KEY",
    );
  // Redirect to backend callback page which will relay the auth code
  // back to the Electron app via fintheon:// deep link
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: getAuthRedirectUrl(mode, relayId),
      skipBrowserRedirect: true,
    },
  });
  if (error) throw error;
  return data;
}

export async function signInWithMagicLink(
  email: string,
  mode: AuthFlowMode = "login",
) {
  if (!supabase)
    throw new Error(
      "Supabase not configured — check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY or VITE_SUPABASE_PUBLISHABLE_KEY",
    );

  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: getAuthRedirectUrl(mode),
      shouldCreateUser: mode === "signup",
      data: { source: "fintheon-magic-link", mode },
    },
  });
  if (error) throw error;
}

export async function signInWithEmailPassword({
  email,
  password,
  mode,
}: EmailPasswordAuthInput): Promise<{ needsConfirmation: boolean }> {
  if (!supabase)
    throw new Error(
      "Supabase not configured — check VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY or VITE_SUPABASE_PUBLISHABLE_KEY",
    );

  if (mode === "signup") {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: getAuthRedirectUrl(mode),
        data: { source: "fintheon-password", mode },
      },
    });
    if (error) throw error;
    return { needsConfirmation: !data.session };
  }

  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return { needsConfirmation: false };
}

/** Get current session access token (for Bearer header to backend) */
export async function getAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export async function signOut() {
  if (!supabase) return;
  await supabase.auth.signOut();
}

export async function createDesktopWebSessionHandoffUrl(
  returnTo = "/",
): Promise<string> {
  if (!supabase) {
    throw new Error("Supabase not configured");
  }
  if (!isElectronRuntime()) {
    throw new Error("Session handoff is only available in desktop");
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("No desktop session to hand off");

  const apiBase = getRuntimeApiBase();
  const response = await fetch(`${apiBase}/api/auth/session-handoff`, {
    body: JSON.stringify({
      appOrigin: getConfiguredAuthOrigin(),
      returnTo,
    }),
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    method: "POST",
  });
  const data = (await response.json().catch(() => null)) as {
    error?: string;
    url?: string;
  } | null;
  if (!response.ok || !data?.url) {
    throw new Error(data?.error ?? "Unable to create web session handoff");
  }
  return data.url;
}

function getAuthRedirectUrl(mode: AuthFlowMode, relayId?: string): string {
  if (isElectronRuntime()) {
    return buildAuthCallbackUrl({
      callbackPath: "/api/auth/supabase/callback",
      currentHref: window.location.href,
      mode,
      preferredOrigin: getConfiguredAuthOrigin(),
      relayId,
      surface: "desktop",
    });
  }

  return buildAuthCallbackUrl({
    currentHref: window.location.href,
    mode,
    preferredOrigin: import.meta.env.VITE_FINTHEON_AUTH_ORIGIN,
    surface: "web",
  });
}

function getConfiguredAuthOrigin(): string {
  return import.meta.env.VITE_FINTHEON_AUTH_ORIGIN || DEFAULT_AUTH_ORIGIN;
}
